import React, { useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { newSelect } from '../../ui/newSelect';

const barOptions = (categories, data, title) => ({
  chart: { type: 'column', height: 220, backgroundColor: 'transparent' },
  title: { text: undefined },
  credits: { enabled: false },
  xAxis: { categories, labels: { style: { fontSize: '11px' } } },
  yAxis: { title: { text: title }, allowDecimals: false, min: 0 },
  legend: { enabled: false },
  series: [{ name: title, data, color: '#1B4332' }],
  tooltip: { pointFormat: '<b>{point.y}</b> clients' },
});

/**
 * Plan / FTE / add-on mix for programme or firm dashboards.
 * @param {{ mix?: { total?: number, plans?: object[], fteBands?: object[], addOns?: object[] }, title?: string }} props
 */
const ClientMixPanel = ({ mix, title = 'Client mix' }) => {
  const [addonFilter, setAddonFilter] = useState('all');
  const total = mix?.total || 0;
  const plans = mix?.plans || [];
  const fteBands = mix?.fteBands || [];
  const addOns = mix?.addOns || [];

  const addonOptions = useMemo(
    () => [{ value: 'all', label: 'All add-ons' }, ...addOns.map((a) => ({ value: a.id, label: a.label || a.id }))],
    [addOns]
  );

  const filteredAddon = addonFilter === 'all' ? null : addOns.find((a) => a.id === addonFilter);

  if (!total && !plans.length && !fteBands.length && !addOns.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground py-4'>No plan / FTE / add-on signals on client accounts yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='grid md:grid-cols-2 gap-4'>
        <Card>
          <CardHeader className='pb-1'>
            <CardTitle className='text-base'>Plan mix</CardTitle>
            <p className='text-xs text-muted-foreground font-normal'>{total} non-demo clients</p>
          </CardHeader>
          <CardContent>
            {plans.length ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={barOptions(
                  plans.map((p) => p.label || p.id),
                  plans.map((p) => p.count),
                  'Clients'
                )}
              />
            ) : (
              <p className='text-sm text-muted-foreground py-6'>No plan data</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-1'>
            <CardTitle className='text-base'>FTE band</CardTitle>
            <p className='text-xs text-muted-foreground font-normal'>Small vs larger customers</p>
          </CardHeader>
          <CardContent>
            {fteBands.length ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={barOptions(
                  fteBands.map((p) => p.label || p.id),
                  fteBands.map((p) => p.count),
                  'Clients'
                )}
              />
            ) : (
              <p className='text-sm text-muted-foreground py-6'>No FTE band data</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className='pb-2'>
          <div className='flex flex-wrap items-end justify-between gap-3'>
            <div>
              <CardTitle className='text-base'>Add-on take-up</CardTitle>
              <p className='text-xs text-muted-foreground font-normal mt-1'>Share of non-demo clients with each add-on</p>
            </div>
            {addOns.length > 0 ? (
              <newSelect
                value={addonFilter}
                onChange={(v) => setAddonFilter(v || 'all')}
                options={addonOptions}
                placeholder='Filter add-on'
                className='w-56'
              />
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {addOns.length === 0 ? (
            <p className='text-sm text-muted-foreground py-4'>No add-ons recorded on these clients.</p>
          ) : filteredAddon ? (
            <div className='flex flex-wrap items-center gap-3 py-2'>
              <Badge variant='default'>{filteredAddon.label}</Badge>
              <span className='text-2xl font-semibold tabular-nums'>{filteredAddon.count}</span>
              <span className='text-sm text-muted-foreground'>
                of {total} ({filteredAddon.pct}%)
              </span>
            </div>
          ) : (
            <div className='space-y-2'>
              {addOns.map((a) => (
                <div key={a.id} className='flex items-center gap-3'>
                  <div className='w-40 text-sm font-medium truncate'>{a.label}</div>
                  <div className='flex-1 h-2 rounded bg-muted overflow-hidden'>
                    <div className='h-full bg-foreground/80' style={{ width: `${Math.min(100, a.pct || 0)}%` }} />
                  </div>
                  <div className='w-24 text-right text-sm tabular-nums text-muted-foreground'>
                    {a.count} · {a.pct}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientMixPanel;
